import Box from "@mui/material/Box";
import React, { useEffect, useRef } from "react";
import { UserChatMessage } from "../../../../../models/userChatMessage";
import { useAuth } from "../../../../Reusable/AuthProvider";
import { useChat } from "../../../../Reusable/UseUserChat";
import { useWebSocket } from "../../../../Reusable/useWebSocket";
import ChatBox from "../ChatBox";
import ChatBubble from "../ChatBubble";

export default function ChatContainer() {
  const {
    openChats,
    minimizedChats,
    getConversation,
    fetchMessages,
    conversationCache,
    messageCache,
    setMessageCache,
    openChat,
  } = useChat();
  const { user } = useAuth();
  const { subscribe } = useWebSocket();

  // Ajout des refs pour garder les valeurs à jour
  const openChatsRef = useRef(openChats);
  const minimizedChatsRef = useRef(minimizedChats);

  useEffect(() => {
    openChatsRef.current = openChats;
  }, [openChats]);

  useEffect(() => {
    minimizedChatsRef.current = minimizedChats;
  }, [minimizedChats]);

  // Charger toutes les conversations/messages nécessaires au montage
  useEffect(() => {
    [...openChats, ...minimizedChats].forEach((id) => {
      if (!conversationCache[id]) {
        getConversation(id, () => {});
      }
      if (!messageCache[id]) {
        fetchMessages(id, 0);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openChats, minimizedChats]);

  // Souscription WebSocket centralisée pour tous les chats ouverts/minimisés
  const subscriptionRef = useRef<any>(null);

  useEffect(() => {
    if (!user?.id) return;

    // Subscribe to a single topic for all user chat messages
    if (!subscriptionRef.current) {
      const dest = `/topic/user-chat-message/${user.id}`;
      subscriptionRef.current = subscribe(dest, (frame) => {
        const msg = JSON.parse(frame.body) as UserChatMessage;
        const id = msg.conversation.usersKeyOrCertificationId;
        setMessageCache((prev) => {
          const old = prev[id]?.messages || [];
          return {
            ...prev,
            [id]: {
              messages: [...old, msg],
              hasMore: prev[id]?.hasMore ?? true,
              page: prev[id]?.page ?? 0,
            },
          };
        });
        // Utiliser les refs pour avoir les valeurs à jour
        if (
          !openChatsRef.current.includes(id) &&
          !minimizedChatsRef.current.includes(id)
        ) {
          openChat(id);
          fetchMessages(id, 0, true);
        }
      });
    }

    // Cleanup on unmount
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        right: (theme) => theme.spacing(4),
        zIndex: (theme) => theme.zIndex.tooltip,
        display: "flex",
        alignItems: "flex-end",
        gap: 2,
      }}
    >
      {/* Open chat windows */}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
        {openChats.map((usersKeyOrCertificationId) => (
          <ChatBox
            key={usersKeyOrCertificationId}
            usersKeyOrCertificationId={usersKeyOrCertificationId}
          />
        ))}
      </Box>

      {/* Minimized chat bubbles */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexDirection: "column",
          paddingBottom: "16px",
        }}
      >
        {minimizedChats.map((usersKeyOrCertificationId) => (
          <ChatBubble
            key={usersKeyOrCertificationId}
            usersKeyOrCertificationId={usersKeyOrCertificationId}
          />
        ))}
      </Box>
    </Box>
  );
}
